import {
  Avatar,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import CableIcon from '@mui/icons-material/Cable';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import SecurityIcon from '@mui/icons-material/Security';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/AuthProvider';

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
}

/** `withHelp`: the app bar has no room for its Help button (below `sm`) — Help is here. */
export function UserMenu({ withHelp = false }: { withHelp?: boolean } = {}) {
  const auth = useAuth();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (!auth.user) return null;

  const fullName = `${auth.user.first_name} ${auth.user.last_name}`.trim() || auth.user.email;
  const inits = initials(fullName);

  return (
    <>
      {/* White-alpha on the navy bar, like the scope chip (0.22 rest, 0.30 on
          hover / keyboard focus): Signal Red is reserved for signal, and white
          on it was 3.4:1. `inherit` keeps the focus ripple white on navy. */}
      <IconButton
        color="inherit"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          p: 0,
          '&:hover .MuiAvatar-root, &.Mui-focusVisible .MuiAvatar-root': {
            bgcolor: 'rgba(255,255,255,0.3)',
          },
        }}
      >
        <Avatar sx={{ width: 36, height: 36, bgcolor: 'rgba(255,255,255,0.22)', color: '#ffffff' }}>
          {inits}
        </Avatar>
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => { setAnchor(null); navigate(`/users/${auth.user!.id}`); }}
        >
          <ListItemText
            primary={fullName}
            secondary={auth.user.email}
            primaryTypographyProps={{ color: 'primary' }}
            secondaryTypographyProps={{ variant: 'caption' }}
          />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { setAnchor(null); navigate('/auth/mfa/enroll'); }}>
          <ListItemIcon><SecurityIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Security &amp; MFA</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); navigate('/mcp-connections'); }}>
          <ListItemIcon><CableIcon fontSize="small" /></ListItemIcon>
          <ListItemText>MCP connections</ListItemText>
        </MenuItem>
        {withHelp && (
          <MenuItem onClick={() => { setAnchor(null); navigate('/help'); }}>
            <ListItemIcon><HelpOutlineIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Help</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => { setAnchor(null); void auth.logout(); }}>
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
          <Typography>Sign out</Typography>
        </MenuItem>
      </Menu>
    </>
  );
}
